import { ResourceConfigurationInterface } from '@cbsinteractive/avia-js';
import { ResourceAdViewabilityTrackingInterface } from '@cbsinteractive/avia-js/dist/types/iface/ResourceAdViewabilityTrackingInterface';
import { ResourceOverridesInterface } from '@cbsinteractive/avia-js/dist/types/iface/ResourceOverridesInterface';
import { urlAction } from 'library/ts/model/enums/urlState';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { IResourceConfigLocationDrm } from 'library/ts/player/adapter/interfaces/resource/config/location/drm/IResourceConfigLocationDrm';
import { ISessionStore } from 'library/ts/utilities/interfaces/ISessionStore';
import { TLoggable } from 'library/ts/utilities/logger/types/TLoggable';
import SessionStore from 'library/ts/utilities/SessionStore';
import { IChannelEpisode } from 'skin/ts/Skin/interfaces/live/IChannelInterfaces';
import { ParentEnvelope } from 'tag/ts/smart_tag/model/parent/envelope/ParentEnvelope';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { FreewheelDaiApiRequestFactory } from 'tag/ts/tag/ads/freewheel/factories/FreewheelDaiApiRequestFactory';
import { FreewheelDaiSdkRequestFactory } from 'tag/ts/tag/ads/freewheel/factories/FreewheelDaiSdkRequestFactory';
import { FreewheelDaiSsbRequestFactory } from 'tag/ts/tag/ads/freewheel/factories/FreewheelDaiSsbRequestFactory';
import { FreewheelImaRequestFactory } from 'tag/ts/tag/ads/freewheel/factories/FreewheelImaRequestFactory';
import { IFreewheelRequest } from 'tag/ts/tag/ads/freewheel/interfaces/IFreewheelRequest';
import { IAdSetupInfo } from 'tag/ts/tag/interfaces/ads/IAdSetupInfo';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IContextMetadata } from 'tag/ts/tag/interfaces/metadata/IContextMetadata';
import { AdSetupDefinition } from 'tag/ts/tag/model/type/ads/AdSetupDefinition';
import { DefaultAviaConstants } from 'tag/ts/tag/player/adapters/avia/constants/DefaultAviaConstants';
import { IAssetPlaybackUtility } from 'tag/ts/tag/player/adapters/avia/interfaces/IAssetPlaybackUtility';
import { IResourceConfigBuilder } from 'tag/ts/tag/player/adapters/avia/interfaces/IResourceConfigBuilder';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description Base class for VTG resource configuration builders using Builder pattern
 *
 * **Builder Pattern Implementation:**
 * Provides common functionality for creating VTG ResourceConfiguration objects
 * across different platform types (Desktop/Mobile Web vs HTML5) and content
 * embed types (PID, CID, Asset URL).
 *
 * **Architecture:**
 * - **Two-Level Builder Hierarchy**: EmbedType → AssetEmbedType
 * - **Shared Configuration Logic**: Common resource settings across all platforms
 * - **Utility Integration**: Works with PIDPlaybackUtility, AssetUrlPlaybackUtility
 * - **Ad Configuration**: Handles Freewheel DAI, IMA, SSB ad setup
 * - **DRM Support**: Configures content protection for premium content
 *
 * **Configuration Areas:**
 * - **Media URLs**: Stream endpoints and fallback URLs
 * - **DRM Settings**: Widevine, PlayReady, FairPlay license configuration
 * - **Ad Parameters**: Ad server URLs, targeting, and tracking setup
 * - **Quality Settings**: Bitrate limits and adaptive streaming configuration
 * - **Thumbnail Tracks**: Preview image timeline configuration
 *
 * **Platform Abstraction:**
 * Allows subclasses to implement platform-specific logic while sharing
 * common configuration patterns, reducing code duplication and ensuring
 * consistent resource setup across deployment targets.
 */
export abstract class AbstractResourceConfigBuilder extends AbstractTagClass implements IResourceConfigBuilder {

	static readonly #WARN_INVALID_ARGS = 'missing/invalid arguments... returning resource unmodified';

	/**
	 * @description Constant message for invalid arguments warning
	 */
	private static readonly INVALID_ARGS_MSG = 'missing/invalid arguments... returning resource unmodified';

	/**
	 * @description Used by subtypes to create ResourceConfiguration
	 */
	protected assetPlaybackUtil: IAssetPlaybackUtility;

	protected constructor( argument: ITagAbstractClassArgument = {
		name: 'AbstractResourceConfigBuilder',
		tag: undefined
	} ) {
		super(
			{
				...argument
			}
		);
	}

	/**
	 * @description Run cleanup logic
	 */
	clean = (): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve, reject ) => {
				this.assetPlaybackUtil.clean()
					.then( () => {
						this.assetPlaybackUtil = null;
						resolve( {
							msg: 'clean successful',
							success: true
						} );
					} )
					.catch( ( error ) => {
						this.logger.warn( 'clean', 'Error:', error );
						reject( {
							msg: 'clean failed',
							success: false,
							error: error
						} );
					} );
			}
		);
	};

	/**
	 * @description Returns filled resource configuration
	 * @returns {Promise<IPromiseResolveGeneric & {resource: IResourceConfig}>}
	 */
	abstract getResult(): Promise<IPromiseResolveGeneric & { resource: ResourceConfigurationInterface }>;

	/**
	 * @description Sets resource metadata for tracking
	 * @param {ResourceConfigurationInterface} resource
	 * @returns {Promise<ResourceConfigurationInterface>}
	 */
	protected abstract setContextMetadata( resource: ResourceConfigurationInterface ): Promise<ResourceConfigurationInterface>;

	/**
	 * @description Adaptive Bitrate settings
	 * @param {ResourceConfigurationInterface} resource
	 * @param {number} bitrate
	 * @returns {Promise<ResourceConfigurationInterface>}
	 */
	protected setStartBitrate( resource: ResourceConfigurationInterface, bitrate: number = DefaultAviaConstants.getInstance().startBitrate ): Promise<ResourceConfigurationInterface> {
		return new Promise(
			( resolve, reject ) => {
				if (
					this.utils.Type.isEmpty( resource ) ||
					!this.utils.Type.isNumber( bitrate )
				) {
					this?.logger?.warn( 'setStartBitrate', 'missing/invalid arguments... resolving resource unmodified', 'resource:', resource, 'bitrate:', bitrate );
					resolve( resource );
					return;
				}

				resource.playback.abr.startBitrate = bitrate;
				this?.logger?.log( 'setStartBitrate', 'set resource.playback.abr.startBitrate:', resource.playback.abr.startBitrate );

				resolve( resource );
			}
		);
	}

	/**
	 * @description Sets resource config overrides
	 * @param {ResourceConfigurationInterface} resource
	 * @param {ResourceOverridesInterface} overrides
	 * @returns {Promise<ResourceConfigurationInterface>}
	 */
	protected setOverrides( resource: ResourceConfigurationInterface, overrides: ResourceOverridesInterface = DefaultAviaConstants.getInstance().overrides ): Promise<ResourceConfigurationInterface> {
		return new Promise(
			( resolve, reject ) => {
				if (
					this.utils.Type.isEmpty( resource ) ||
					this.utils.Type.isEmpty( overrides ) ||
					!this.utils.Type.isObject( overrides )
				) {
					this?.logger?.warn( 'setOverrides', AbstractResourceConfigBuilder.#WARN_INVALID_ARGS, 'resource:', resource, 'overrides:', overrides );
					return resource;
				}

				resource.overrides = overrides;
				this?.logger?.log( 'setOverride', 'set resource.overrides:', resource.overrides );

				resolve( resource );
			}
		);
	}

	/**
	 * @description Sets DRM in resource config. Defaults to params.CONTENT.drm
	 * @param {ResourceConfigurationInterface} resource
	 * @param {IResourceConfigLocationDrm} drm
	 * @return {ResourceConfigurationInterface}
	 */
	protected setDrm( resource: ResourceConfigurationInterface, drm: IResourceConfigLocationDrm = this.params?.CONTENT?.drm ): ResourceConfigurationInterface {
		if (
			this.utils.Type.isEmpty( resource ) ||
			this.utils.Type.isEmpty( drm ) ||
			!this.utils.Type.isObject( drm )
		) {
			this?.logger?.warn( 'setDrm', AbstractResourceConfigBuilder.#WARN_INVALID_ARGS, 'resource:', resource, 'drm:', drm );
			return resource;
		}

		// merge incoming drm object into resource config
		resource.location.drm = this.utils.DeepMerge.deepmerge( resource.location.drm, drm );
		this?.logger?.log( 'setDrm', 'deep merged drm into resource.location.drm...', 'drm:', drm, 'resource.location.drm:', resource.location.drm );

		return resource;
	}

	/**
	 * @description Sets media url in resource config.
	 *
	 * The query string is passed through `utils.URL.escapeQueryDots` (`.` → `%2E`) before the
	 * URL reaches avia: Shaka 4.16+ resolves the manifest type from the URL when avia passes
	 * no mime type to `player.load()`, and a literal dot inside any query value (FreeWheel
	 * `imafw_host=www.x.com`, GPP/TCF consent strings) breaks its extension sniffing, failing
	 * playback with UNABLE_TO_GUESS_MANIFEST_TYPE (Shaka 4000 → avia 2103). `%2E` decodes
	 * byte-identically server-side, so all downstream consumers see unchanged values.
	 *
	 * @param {ResourceConfigurationInterface} resource
	 * @param {string} mediaUrl
	 * @return {ResourceConfigurationInterface}
	 */
	protected setMediaUrl( resource: ResourceConfigurationInterface, mediaUrl: string ): ResourceConfigurationInterface {
		if (
			this.utils.Type.isEmpty( resource ) ||
			this.utils.Type.isEmpty( mediaUrl ) ||
			!this.utils.Type.isString( mediaUrl )
		) {
			this?.logger?.warn( 'setMediaUrl', AbstractResourceConfigBuilder.#WARN_INVALID_ARGS, 'resource:', resource, 'mediaUrl:', mediaUrl );
			return resource;
		}

		resource.location.mediaUrl = this.utils.URL.escapeQueryDots( mediaUrl );
		this?.logger?.log( 'setMediaUrl', 'set resource.location.mediaUrl:', resource.location.mediaUrl );

		return resource;
	}

	/**
	 * @description Sets start time for asset playback. Defaults to params.CONTENT.globalResumeTime.
	 * When urlAction.WITHOUT_RESUME_TIME is in session storage (end-card redirect, including homepage fallback), start time is not applied and the flag is removed.
	 * @param {ResourceConfigurationInterface} resource
	 * @param {number} startTime
	 * @return {ResourceConfigurationInterface}
	 */
	protected setStartTime( resource: ResourceConfigurationInterface, startTime: number = this?.params?.CONTENT?.globalResumeTime ): ResourceConfigurationInterface {
		const sessionStore: ISessionStore = SessionStore.getInstance();
		const withoutResumeTime = sessionStore?.get( urlAction.WITHOUT_RESUME_TIME );
		if ( withoutResumeTime ) {
			this?.logger?.log( 'setStartTime', 'WITHOUT_RESUME_TIME found in session storage, skipping resume time' );
			sessionStore?.remove( urlAction.WITHOUT_RESUME_TIME );
			if ( resource?.playback && !this.utils.Type.isEmpty( resource.playback.startTime ) ) {
				delete resource.playback.startTime;
			}
			return resource;
		}

		if (
			this.utils.Type.isEmpty( resource ) ||
			this.utils.Type.isEmpty( startTime ) ||
			!this.utils.Type.isNumber( startTime ) ||
			startTime < 0
		) {
			this?.logger?.warn( 'setStartTime', AbstractResourceConfigBuilder.#WARN_INVALID_ARGS, 'resource:', resource, 'startTime:', startTime );
			return resource;
		}

		resource.playback.startTime = startTime;
		this?.logger?.log( 'setStartTime', 'set resource.playback.startTime:', resource.playback.startTime );

		return resource;
	}

	/**
	 * @description Sets Video thumbnail track
	 * @param {ResourceConfigurationInterface} resource
	 * @param {string} url
	 * @return {ResourceConfigurationInterface}
	 */
	protected setThumbnailTrackUrl( resource: ResourceConfigurationInterface, url: string ): ResourceConfigurationInterface {
		if (
			this.utils.Type.isEmpty( resource ) ||
			this.utils.Type.isEmpty( url ) ||
			!this.utils.Type.isString( url )
		) {
			this?.logger?.warn( 'setThumbnailTrackUrl', AbstractResourceConfigBuilder.#WARN_INVALID_ARGS, 'resource:', resource, 'url:', url );
			return resource;
		}

		resource.location.thumbnailTrackUrl = url;
		this?.logger?.log( 'setThumbnailTrackUrl', 'set resource.location.thumbnailTrackUrl:', resource.location.thumbnailTrackUrl );

		return resource;
	}

	/**
	 * @description Sets resource.metadata = {}. Overrides current value, if set
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected initMetadata( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		if ( !this.utils.Type.isEmpty( resource?.metadata ) ) {
			this?.logger?.warn( 'initMetadata', 'resource.metadata is not empty... overriding:', JSON.stringify( resource.metadata ) );
		}

		resource.metadata = {};
		this?.logger?.log( 'initMetadata', 'initialized resource.metadata = {}', 'resource:', resource );

		return resource;
	}

	/**
	 * @description Sets a key/value pair in resource.metadata. Initializes metadata object, if unset
	 * @param {ResourceConfigurationInterface} resource
	 * @param {string} key
	 * @param {unknown} value
	 * @return {ResourceConfigurationInterface}
	 */
	protected setMetadataParameter( resource: ResourceConfigurationInterface, key: string, value: unknown ): ResourceConfigurationInterface {
		if (
			this.utils.Type.isEmpty( resource ) ||
			this.utils.Type.isEmpty( key ) ||
			!this.utils.Type.isString( key )
		) {
			this?.logger?.warn( 'setMetadataParameter', AbstractResourceConfigBuilder.#WARN_INVALID_ARGS, 'resource:', resource, 'key:', key, 'value:', value as TLoggable );
			return resource;
		}

		if ( this.utils.Type.isEmpty( resource?.metadata ) ) {
			resource = this.initMetadata( resource );
		}

		resource.metadata[ key ] = value;
		this?.logger?.log( 'setMetadataParameter', `set resource.metadata.${key}:`, value as TLoggable );

		return resource;
	}

	/**
	 * @description Sets resource.ad.csai = {}. Overrides current value, if set
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected initCsai( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		if ( !this.utils.Type.isEmpty( resource?.ad?.csai ) ) {
			this?.logger?.warn( 'initCsai', 'resource.ad.csai is not empty... overriding:', JSON.stringify( resource.ad.csai ) );
		}

		resource.ad.csai = {};
		this?.logger?.log( 'initCsai', 'initialized resource.ad.csai = {}', 'resource:', resource );

		return resource;
	}

	/**
	 * @description Sets resource.ad.csai = null
	 * @return {ResourceConfigurationInterface}
	 * @param resource
	 */
	protected removeCsai( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		if ( this.utils.Type.isEmpty( resource ) ) {
			this?.logger?.warn( 'removeCsai', AbstractResourceConfigBuilder.#WARN_INVALID_ARGS, 'resource:', resource );
			return resource;
		}

		if ( !this.utils.Type.isEmpty( resource?.ad?.csai ) ) {
			this?.logger?.warn( 'removeCsai', 'resource.ad.csai is not empty... removing:', JSON.stringify( resource.ad.csai ) );
		}

		resource.ad.csai = null;
		this?.logger?.log( 'removeCsai', 'set resource.ad.csai = null' );

		return resource;
	}

	/**
	 * @description Sets a key/value pair in resource.ad.csai. Initializes csai object, if unset
	 * @param {ResourceConfigurationInterface} resource
	 * @param {string} key
	 * @param {unknown} value
	 * @return {ResourceConfigurationInterface}
	 */
	protected setCsaiParameter( resource: ResourceConfigurationInterface, key: string, value: unknown ): ResourceConfigurationInterface {
		if (
			this.utils.Type.isEmpty( resource ) ||
			this.utils.Type.isEmpty( key ) ||
			!this.utils.Type.isString( key )
		) {
			this?.logger?.warn( 'setCsaiParameter', AbstractResourceConfigBuilder.#WARN_INVALID_ARGS, 'resource:', resource, 'key:', key, 'value:', value as TLoggable );
			return resource;
		}

		if ( this.utils.Type.isEmpty( resource?.ad?.csai ) ) {
			resource = this.initCsai( resource );
		}

		resource.ad.csai[ key ] = value;
		this?.logger?.log( 'setCsaiParameter', `set resource.ad.csai.${key}:`, value as TLoggable );

		return resource;
	}

	/**
	 * @description Sets resource.ad.ssai = {}. Overrides current value, if set
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected initSsai( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		if ( !this.utils.Type.isEmpty( resource?.ad?.ssai ) ) {
			this?.logger?.warn( 'initSsai', 'resource.ad.ssai is not empty... overriding:', JSON.stringify( resource.ad.ssai ) );
		}

		resource.ad.ssai = {};
		this?.logger?.log( 'initSsai', 'initialized resource.ad.ssai = {}', 'resource:', resource );

		return resource;
	}

	/**
	 * @description Sets resource.ad.ssai = null
	 * @return {ResourceConfigurationInterface}
	 * @param resource
	 */
	protected removeSsai( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		if ( this.utils.Type.isEmpty( resource ) ) {
			this?.logger?.warn( 'removeSsai', AbstractResourceConfigBuilder.#WARN_INVALID_ARGS, 'resource:', resource );
			return resource;
		}

		if ( !this.utils.Type.isEmpty( resource?.ad?.ssai ) ) {
			this?.logger?.warn( 'removeSsai', 'resource.ad.ssai is not empty... removing:', JSON.stringify( resource.ad.ssai ) );
		}

		resource.ad.ssai = null;
		this?.logger?.log( 'removeSsai', 'set resource.ad.ssai = null' );

		return resource;
	}

	/**
	 * @description Sets a key/value pair in resource.ad.ssai. Initializes ssai object, if unset
	 * @param {ResourceConfigurationInterface} resource
	 * @param {string} key
	 * @param {unknown} value
	 * @return {ResourceConfigurationInterface}
	 */
	protected setSsaiParameter( resource: ResourceConfigurationInterface, key: string, value: unknown ): ResourceConfigurationInterface {
		if (
			this.utils.Type.isEmpty( resource ) ||
			this.utils.Type.isEmpty( key ) ||
			!this.utils.Type.isString( key )
		) {
			this?.logger?.warn( 'setSsaiParameter', AbstractResourceConfigBuilder.#WARN_INVALID_ARGS, 'resource:', resource, 'key:', key, 'value:', value as TLoggable );
			return resource;
		}

		if ( this.utils.Type.isEmpty( resource?.ad?.ssai ) ) {
			resource = this.initSsai( resource );
		}

		resource.ad.ssai[ key ] = value;
		this?.logger?.log( 'setSsaiParameter', `set resource.ad.ssai.${key}:`, value as TLoggable );

		return resource;
	}

	/**
	 * @description Sets resource.ad.ssai.adParameters = {}. Overrides current value, if set
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected initSsaiAdParameter( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		if ( this.utils.Type.isEmpty( resource ) ) {
			this?.logger?.warn( 'initSsaiAdParameter', AbstractResourceConfigBuilder.#WARN_INVALID_ARGS, 'resource:', resource );
			return resource;
		}

		if ( this.utils.Type.isEmpty( resource?.ad?.ssai ) ) {
			resource = this.initSsai( resource );
		}

		if ( !this.utils.Type.isEmpty( resource.ad.ssai.adParameters ) ) {
			this?.logger?.warn( 'initSsaiAdParameter', 'resource.ad.ssai.adParameters is not empty... overriding:', JSON.stringify( resource.ad.ssai.adParameters ) );
		}

		resource.ad.ssai.adParameters = {};
		this?.logger?.log( 'initSsaiAdParameter', 'initialized resource.ad.ssai.adParameters={}', 'resource:', resource );

		return resource;
	}

	/**
	 * @description Sets a key/value pair in resource.ad.ssai.adParameters. Initializes ssai, adParameters if unset
	 * @param {ResourceConfigurationInterface} resource
	 * @param {string} key
	 * @param {unknown} value
	 * @return {ResourceConfigurationInterface}
	 */
	protected setSsaiAdParameter( resource: ResourceConfigurationInterface, key: string, value: unknown ): ResourceConfigurationInterface {
		if (
			this.utils.Type.isEmpty( resource ) ||
			this.utils.Type.isEmpty( key ) ||
			!this.utils.Type.isString( key )
		) {
			this?.logger?.warn( 'setSsaiAdParameter', AbstractResourceConfigBuilder.#WARN_INVALID_ARGS, 'resource:', resource, 'key:', key, 'value:', value as TLoggable );
			return resource;
		}

		if ( this.utils.Type.isEmpty( resource?.ad?.ssai?.adParameters ) ) {
			resource = this.initSsaiAdParameter( resource );
		}

		resource.ad.ssai.adParameters[ key ] = value;
		this?.logger?.log( 'setSsaiParameter', `set resource.ad.ssai.adParameters.${key}:`, value as TLoggable );

		return resource;
	}

	/**
	 * @description Sets resource.playback = {}. Overrides current value, if set
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected initPlaybackConfiguration( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		if ( !this.utils.Type.isEmpty( resource?.playback ) ) {
			this?.logger?.warn( 'initPlaybackConfiguration', 'resource.playback is not empty... overriding:', JSON.stringify( resource.playback ) );
		}

		resource.playback = {};
		this?.logger?.log( 'initPlaybackConfiguration', 'initialized resource.playback = {}', 'resource:', resource );

		return resource;
	}

	/**
	 * @description Sets resource.playback.adapter = {}. Overrides current value, if set
	 * @param {ResourceConfigurationInterface} resource
	 * @param {string} id
	 * @param {Record<string, unknown>} options
	 * @return {ResourceConfigurationInterface}
	 */
	protected setResourceAdapterSettings( resource: ResourceConfigurationInterface, id?: string, options?: Record<string, unknown> ): ResourceConfigurationInterface {
		if ( this.utils.Type.isEmpty( resource?.playback ) ) {
			resource = this.initPlaybackConfiguration( resource );
		}
		if ( !this.utils.Type.isEmpty( resource.playback.adapter ) ) {
			this?.logger?.warn( 'setResourceAdapterSettings', 'resource.playback.adapter is not empty... overriding:', JSON.stringify( resource.playback.adapter ) );
		}

		resource.playback.adapter = {};
		if ( id ) {
			resource.playback.adapter.id = id;
		}
		if ( options ) {
			resource.playback.adapter.options = options;
		}

		this?.logger?.log( 'setResourceAdapterSettings', 'set resource.playback.adapter', 'resource:', resource );
		return resource;
	}

	/**
	 * @description Deep merge resource from SmartTag params into SmartTag own resource
	 * @param {ResourceConfigurationInterface} resourceParam
	 * @return {ResourceConfigurationInterface}
	 */
	protected deepMergeResourceFromSmartTagParams( resourceParam: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		if (
			this.utils.Type.isEmpty( this.params?.CONTENT?.resource ) ||
			!this.utils.Type.isObject( this.params?.CONTENT?.resource )
		) {
			this?.logger?.log( 'deepMergeResourceFromSmartTagParams', 'params.CONTENT.resource is unset... returning resource unmodified' );
			return resourceParam;
		}

		this?.logger?.log( 'deepMergeResourceFromSmartTagParams', 'params.CONTENT.resource is set:', this.params.CONTENT.resource );

		// delete empty mediaUrl property from incoming resource override
		if (
			!this.utils.Type.isEmpty( this.params?.CONTENT?.resource?.location ) && 		// if location bucket exists
			this.utils.Type.isObject( this.params?.CONTENT?.resource?.location ) && 		// if it is an object...
			this.utils.Type.isEmpty( this?.params?.CONTENT?.resource?.location?.mediaUrl ) 	// mediaUrl is empty
		) {
			delete this.params.CONTENT.resource.location.mediaUrl; // delete mediaUrl property
			this?.logger?.log( 'deepMergeResourceFromSmartTagParams', 'removed params.CONTENT.resource.location.mediaUrl' );
		}

		// merge resource from page into our resource
		const resourceMerged = this.utils.DeepMerge.deepmerge( resourceParam, this.params?.CONTENT?.resource );
		this?.logger?.log( 'deepMergeResourceFromSmartTagParams', 'deep merged params.CONTENT.resource into resource' );

		if ( this.runtimeVO?.debug?.on === true ) {
			const smartTagResource = this.utils.Object.cloneDeep( resourceParam );
			const initialComparison = this.utils.Compare.compare( smartTagResource, this.params?.CONTENT?.resource );
			const mergedComparison = this.utils.Compare.compare( smartTagResource, resourceMerged );
			this?.logger?.log(
				'\n---',
				'\n smartTagResource resource',
				smartTagResource,
				'\n params-supplied resource',
				this.params?.CONTENT?.resource,
				'\n merged resource',
				resourceMerged,
				'\n initial comparison (Smart Tag generated vs incoming params)',
				initialComparison,
				'\n merged comparison (Smart Tag generated vs merged resource)',
				mergedComparison,
				'\n---'
			);
		}
		return resourceMerged || resourceParam;
	}

	/**
	 * @description Method for sanitizing resource config object for some edge cases
	 * @param {ResourceConfigurationInterface} resource
	 * @returns {ResourceConfigurationInterface}
	 */
	protected sanitizeResource( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		// get ad setup info object
		const adSetupInfo = this.tag?.model?.adUtilities?.adSetupInfo(); // get ad setup info

		// delete unused properties from pre avia 2.0.0 resource config object
		if (
			this.utils.Type.isObject( resource?.ad ) &&
			!this.utils.Type.isEmpty( resource?.ad )
		) {
			delete resource.ad[ 'dai' ];
			delete resource.ad[ 'ima' ];
			this?.logger?.log( 'sanitizeResource', 'deleted resource.ad.dai, resource.ad.ima' );
		}

		// sanitize NFL currentListingTitle
		if ( resource?.metadata?.currentListingTitle ) {
			resource.metadata.currentListingTitle = this.cleanNFL_title( resource?.metadata?.currentListingTitle );
		}

		switch ( adSetupInfo?.setup?.definition ) {
			case AdSetupDefinition.DAI_SSB:
				// kill non-DAI SSB properties in resource config. DAI SSB does not need these.
				resource = this.removeCsai( resource );
				resource = this.removeSsai( resource );
				this?.logger?.log( 'sanitizeResource', 'adSetupInfo.setup.definition = AdSetupDefinition.DAI_SSB... removed unneeded resource.ad.csai, resource.ad.ssai' );
				break;
		}

		return resource;
	}

	/**
	 * @description WARNING: One-off overrides for custom test use cases required by VidOps etc.
	 * @param {ResourceConfigurationInterface} resource
	 * @returns {ResourceConfigurationInterface}
	 */
	protected testOverrides( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		// filter by parent application envelope
		switch ( this.runtimeVO.parentEnvelope ) {
			case ParentEnvelope.TEST_WWW_PPLUS:
				// Talia use case => force advanced codecs on test-www environment
				if ( this.apiMetadata.contentId === 'RNwC4Zv1QWvw8HquUke7sRpdycsukVhf' ) {
					resource.playback.enableAdvancedCodecs = true;
				}
				if ( this.apiMetadata.contentId === 'jtTbyEmO_SEVlebfttPARspVeSy7CKZu' ) {
					resource.playback.enableAdvancedCodecs = true;
					resource.playback.preferSpatialAudio = true;
				}
				break;
		}

		// get top URL hlsShaka query parameter
		const useShaka: boolean = this.utils.Convert.stringToBoolean( this.utils.URL.getQueryField( 'useShaka', this?.runtimeVO?.top?.url ), false );

		// if useShaka=true in URL, force shaka adapter
		if ( useShaka === true ) {
			// initialize a playback object, if unset
			if ( this.utils.Type.isEmpty( resource?.playback ) ) {
				resource.playback = {};
			}
			// set shaka adapter using deep merge
			resource.playback = this.utils.DeepMerge.deepmerge(
				resource.playback,
				{
					adapter: {
						id: 'shaka'
					}
				}
			);
		}

		return resource;
	}

	/**
	 * @description Sets a tracking object in resource.ad.viewabilityTracking
	 * @return {ResourceConfigurationInterface}
	 * @param resource
	 * @param trackingObj
	 */
	protected setViewabilityTracking( resource: ResourceConfigurationInterface, trackingObj: ResourceAdViewabilityTrackingInterface ): ResourceConfigurationInterface {
		if (
			this.utils.Type.isEmpty( resource ) ||
			this.utils.Type.isEmpty( trackingObj ) ||
			!this.utils.Type.isObject( trackingObj )
		) {
			this?.logger?.warn( 'setViewabilityTracking', AbstractResourceConfigBuilder.#WARN_INVALID_ARGS, 'resource:', resource, 'trackingObj:', trackingObj );
			return resource;
		}

		if ( this.utils.Type.isEmpty( resource?.ad ) ) {
			resource.ad = {};
			this?.logger?.log( 'setViewabilityTracking', 'initialized resource.ad={}' );
		}

		resource.ad.viewabilityTracking = trackingObj;
		this?.logger?.log( 'setViewabilityTracking', 'set resource.ad.viewabilityTracking:', resource.ad.viewabilityTracking );

		return resource;
	}

	/**
	 * @description removes NFl from title - just need game title
	 * @param title
	 */
	public cleanNFL_title = ( title: string = '' ) => {
		if ( title?.includes( 'NFL on CBS -' ) ) {
			const parts: string[] = title?.split( '-' );
			if ( parts.length === 2 ) {
				return parts[ 1 ]?.replace( '-', '' )?.trim();
			}
		}
		return title;
	};

	/**
	 * @description Configures NFL LTS settings in resource config
	 * @param {ResourceConfigurationInterface} resource
	 * @returns {ResourceConfigurationInterface}
	 */
	protected configureNflLts( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		// Check if NFL LTS configuration is not required. Exit without configuration
		if ( !this?.params?.CONTENT?.channelInfo?.isLTSEnabled || !this?.params?.CONTENT?.channelInfo?.isOptinRequired ) {
			this?.logger?.log( 'configureNflLts', 'NFL LTS configuration not required' );
			return resource;
		}

		// Continue with NFL LTS configuration
		const sessionStore: ISessionStore = SessionStore.getInstance();
		const nflLTSOptIn = this?.params?.USER?.nflLTS?.optIn;
		const nflLTSOptInTime = this?.params?.USER?.nflLTS?.optInTime;
		const episode: IChannelEpisode = this?.params?.CONTENT?.channelInfo?.currentListing?.[ 0 ] || sessionStore?.get( 'episode' );
		const metadata: IContextMetadata = this?.params?.CONTEXT?.metadata;
		const gameStartTimestamp = episode?.gameStartTimestamp;
		const ctaText = sessionStore?.get( 'sfbOptionTriggerLabel' ) || '';

		// Patch resources Metadata with LTS & SFB tracking information
		if ( this.utils.Type.isObject( resource?.metadata ) ) {
			resource.metadata.ltsOptInStatus ??= `${nflLTSOptIn || false}`;
			resource.metadata.currentListingTitle ??= this.cleanNFL_title( episode?.title || metadata?.episodeTitle );
			resource.metadata.ctaText ??= ctaText || '';
		}

		// Check for invalid gameStartTimestamp. Exit without configuration
		if ( !this.utils.Type.isNumber( gameStartTimestamp ) ) {
			this?.logger?.warn( 'configureNflLts', 'gameStartTimestamp is missing/invalid. channelInfo:', this?.params?.CONTENT?.channelInfo );
			return resource;
		}
		const optedInBeforeGameStart = nflLTSOptIn && nflLTSOptInTime < gameStartTimestamp;

		// Check if the USER Opted in before the game started. Exit without configuration
		if ( optedInBeforeGameStart ) {
			this?.logger?.log( 'configureNflLts', 'user opted in before game start... allowing scrubbing to entire buffer' );
			return resource;
		}

		// Check for invalid playback configuration object in resource. Exit without configuration
		if ( !this.utils.Type.isObject( resource?.playback ) ) {
			this?.logger?.warn( 'configureNflLts', 'user opted in after game start... but the playback configuration object is missing/invalid:, playback', resource?.playback );
			return resource;
		}

		// If the USER Opted in after the game started OR did not opt in, patch resources with restrictive configuration
		resource.playback.lts ??= {};
		resource.playback.lts.restrictionTimestamp = -1; // limit scrub to stream join time
		resource.playback.lts.threshold = 300; // give user scrubbing control 5 minutes after joining stream
		this?.logger?.log( 'configureNflLts', 'user is not opted in for current game... restricted scrubbing to stream join time' );
		return resource;
	}

	/**
	 * @description Sets apiKey as SSAI parameter
	 * @param {ResourceConfigurationInterface} resource
	 * @param {string} apiKey
	 * @return {ResourceConfigurationInterface}
	 */
	protected setApiKey( resource: ResourceConfigurationInterface, apiKey: string ): ResourceConfigurationInterface {
		return this.setSsaiParameter( resource, 'apiKey', apiKey );
	}

	/**
	 * @description Sets assetKey as SSAI parameter
	 * @param {ResourceConfigurationInterface} resource
	 * @param {string} assetKey
	 * @return {ResourceConfigurationInterface}
	 */
	protected setAssetKey( resource: ResourceConfigurationInterface, assetKey: string ): ResourceConfigurationInterface {
		return this.setSsaiParameter( resource, 'assetKey', assetKey );
	}

	/**
	 * @description Sets isLive as SSAI parameter
	 * @param {ResourceConfigurationInterface} resource
	 * @param {boolean} isLive
	 * @return {ResourceConfigurationInterface}
	 */
	protected setIsLive( resource: ResourceConfigurationInterface, isLive: boolean ): ResourceConfigurationInterface {
		return this.setSsaiParameter( resource, 'isLive', isLive );
	}

	/**
	 * @description Sets authToken as SSAI parameter
	 * @param {ResourceConfigurationInterface} resource
	 * @param {string} authToken
	 * @return {ResourceConfigurationInterface}
	 */
	protected setAuthToken( resource: ResourceConfigurationInterface, authToken: string ): ResourceConfigurationInterface {
		return this.setSsaiParameter( resource, 'authToken', authToken );
	}

	/**
	 * @description Sets contentSourceId as SSAI parameter
	 * @param {ResourceConfigurationInterface} resource
	 * @param {string|number} contentSourceId
	 * @return {ResourceConfigurationInterface}
	 */
	protected setContentSourceId( resource: ResourceConfigurationInterface, contentSourceId: string | number ): ResourceConfigurationInterface {
		return this.setSsaiParameter( resource, 'contentSourceId', contentSourceId );
	}

	/**
	 * @description Sets videoId as SSAI parameter
	 * @param {ResourceConfigurationInterface} resource
	 * @param {string} videoId
	 * @return {ResourceConfigurationInterface}
	 */
	protected setVideoId( resource: ResourceConfigurationInterface, videoId: string ): ResourceConfigurationInterface {
		return this.setSsaiParameter( resource, 'videoId', videoId );
	}

	/**
	 * @description Sets format as SSAI parameter
	 * @param {ResourceConfigurationInterface} resource
	 * @param {string} format
	 * @return {ResourceConfigurationInterface}
	 */
	protected setFormat( resource: ResourceConfigurationInterface, format: string ): ResourceConfigurationInterface {
		return this.setSsaiParameter( resource, 'format', format );
	}

	/**
	 * @description Sets filterOutPremiumAudioCodecs as SSAI parameter
	 * @param {ResourceConfigurationInterface} resource
	 * @param {boolean} canFilter
	 * @return {ResourceConfigurationInterface}
	 */
	protected setFilterOutPremiumAudioCodecs( resource: ResourceConfigurationInterface, canFilter: boolean ): ResourceConfigurationInterface {
		return this.setSsaiParameter( resource, 'filterOutPremiumAudioCodecs', canFilter );
	}

	/**
	 * @description Sets useApi as SSAI parameter
	 * @param {ResourceConfigurationInterface} resource
	 * @param {boolean} useApi
	 * @return {ResourceConfigurationInterface}
	 */
	protected setUseApi( resource: ResourceConfigurationInterface, useApi: boolean ): ResourceConfigurationInterface {
		return this.setSsaiParameter( resource, 'useApi', useApi );
	}

	/**
	 * @description Sets enableNonce as SSAI parameter
	 * @param {ResourceConfigurationInterface} resource
	 * @param {boolean} enableNonce
	 * @return {ResourceConfigurationInterface}
	 */
	protected setEnableNonce( resource: ResourceConfigurationInterface, enableNonce: boolean ): ResourceConfigurationInterface {
		return this.setSsaiParameter( resource, 'enableNonce', !!enableNonce ? 'true' : 'false' );
	}

	/**
	 * @description Sets cust_params as SSAI ad parameter
	 * @param {ResourceConfigurationInterface} resource
	 * @param {string} custParams
	 * @return {ResourceConfigurationInterface}
	 */
	protected setCustParams( resource: ResourceConfigurationInterface, custParams: string ): ResourceConfigurationInterface {
		return this.setSsaiAdParameter( resource, 'cust_params', custParams );
	}

	/**
	 * @description Sets resource.ad.custParams (non-SSAI)
	 * @param {ResourceConfigurationInterface} resource
	 * @param {string} custParams
	 * @return {ResourceConfigurationInterface}
	 */
	protected setAdCustParams( resource: ResourceConfigurationInterface, custParams: string ): ResourceConfigurationInterface {
		if (
			this.utils.Type.isEmpty( resource ) ||
			this.utils.Type.isEmpty( custParams )
		) {
			this?.logger?.warn( 'setAdCustParams', AbstractResourceConfigBuilder.#WARN_INVALID_ARGS, 'resource:', resource, 'custParams:', custParams );
			return resource;
		}

		if ( this.utils.Type.isEmpty( resource?.ad ) ) {
			resource.ad = {};
			this?.logger?.log( 'setAdCustParams', 'initialized resource.ad={}' );
		}

		( resource.ad as Record<string, unknown> ).custParams = custParams;
		this?.logger?.log( 'setAdCustParams', 'set resource.ad.custParams:', custParams );

		return resource;
	}

	/**
	 * @description Sets customUiOptions as SSAI parameter
	 * @param {ResourceConfigurationInterface} resource
	 * @param {object} options
	 * @return {ResourceConfigurationInterface}
	 */
	protected setCustomUiOptions( resource: ResourceConfigurationInterface, options: object ): ResourceConfigurationInterface {
		return this.setSsaiParameter( resource, 'customUiOptions', options );
	}

	/**
	 * @description Sets DAI parameters from Millstone in resource as SSAI ad parameters
	 * @see {@link https://paramountplus.atlassian.net/browse/CBSVIDEO-6076}
	 * @param {IResourceConfig} resource
	 * @param {IMillstoneDAIParams} daiParams
	 * @returns {IResourceConfig}
	 */
	protected setMillstoneDAIParams( resource: ResourceConfigurationInterface, daiParams = this.tag?.millstoneUtilities?.getDaiParams() ): ResourceConfigurationInterface {
		// validate millstone
		if ( this.utils.Type.isEmpty( daiParams?.daiCmsId ) ) {
			this?.logger?.warn( 'setMillstoneDAIParams:', 'daiCmsId from millstone is invalid... exiting', 'daiParams:', daiParams, resource );
			return resource;
		}

		Object.keys( daiParams )
			.filter( ( key ) => {
				if ( this.utils.Type.isEmpty( daiParams[ key ] ) ) {
					this?.logger?.log( 'setMillstoneDAIParams:', 'filtering out param with unset value...', 'key:', key );
					return false;
				}
				return true;
			} )
			.forEach( ( key ) => resource = this.setSsaiAdParameter( resource, key, daiParams[ key ] ) );
		this?.logger?.log( 'setMillstoneDAIParams:', 'set daiParams as SSAI ad parameters', 'daiParams:', daiParams, 'resource:', resource );
		return resource;
	}

	/**
	 * @description Creates instance of IFreewheelRequest based off adSetupInfo
	 * @param {IAdSetupInfo} adSetupInfo
	 * @returns {IFreewheelRequest}
	 */
	protected createFreewheelRequest( adSetupInfo: IAdSetupInfo ): IFreewheelRequest {
		switch ( adSetupInfo?.setup?.definition ) {
			case AdSetupDefinition.DAI_SDK:
				return new FreewheelDaiSdkRequestFactory( { tag: this.tag } ).createRequest();

			case AdSetupDefinition.DAI_SSB:
				return new FreewheelDaiSsbRequestFactory( { tag: this.tag } ).createRequest();

			case AdSetupDefinition.DAI_API:
				return new FreewheelDaiApiRequestFactory( { tag: this.tag } ).createRequest();

			case AdSetupDefinition.IMA:
			case AdSetupDefinition.LINEAR_AD_TRACKING:
				return new FreewheelImaRequestFactory( { tag: this.tag } ).createRequest();

			case AdSetupDefinition.NONE:
				return undefined;

			default:
				this.logger.warn( 'createRequest', 'ad setup definition not recognized, cannot init FreewheelRequest', 'adSetupInfo:', adSetupInfo );
				return undefined;
		}
	}
}